import { type ReactNode, useMemo, useState } from "react";
import { api } from "../../convex-api/api";
import { useQueryWithState, type QueryState } from "../../hooks/useQueryWithState";
import { EmptyState, ErrorState, LoadingState } from "../states";
import "./MarkovWorkspace.css";

type MarkovSessionFilter = "asia" | "us" | "all";
type MarkovWorkspaceTab = "regime-transitions" | "session-states";

type KronosTimeframe = {
  method?: string;
  direction?: string;
  confidence?: number;
  predicted_open?: number;
  predicted_close?: number;
  predicted_high?: number;
  predicted_low?: number;
  predicted_range?: number;
  input_anchor_source?: string;
  anchor_status?: string;
  anchor_rebased?: boolean;
  valid?: boolean;
  invalid_reason?: string;
};

type MarkovTimeframe = {
  regime?: string;
  probability?: number;
  confidence?: number;
  mean_return?: number;
  method?: string;
};

type MarkovSessionStatus = "active" | "pending" | "closed" | "stale" | "unknown" | string;

type MarkovSymbolState = {
  symbol: string;
  session?: string;
  date?: string;
  source?: string;
  created_at?: number;
  probability_type?: "filtered" | string;
  contract_version?: string;
  markov_source_scope?: "session" | "global" | string;
  markov_session_status?: MarkovSessionStatus;
  markov_source_as_of?: number;
  markov_session_start_ms?: number | null;
  markov_session_end_ms?: number | null;
  kronos?: {
    daily?: KronosTimeframe;
    four_hour?: KronosTimeframe;
    hourly?: KronosTimeframe;
  };
  daily?: MarkovTimeframe;
  four_hour?: MarkovTimeframe;
  hourly?: MarkovTimeframe;
  composite?: MarkovTimeframe;
};

type MarkovQuality = {
  sourceLabel: string;
  warning?: string;
};

export function markovQuality(row: MarkovSymbolState): MarkovQuality {
  const methods = [row.daily?.method, row.four_hour?.method, row.hourly?.method].filter(Boolean) as string[];
  const usesLocalFallback = methods.some((method) => method.startsWith("local_hmm"));
  const allTimeframesShareMethod = methods.length >= 3 && new Set(methods).size === 1;
  const source = row.source ?? "live";

  if (row.markov_session_status === "pending") {
    return {
      sourceLabel: `${source} · global live regime`,
      warning: "PENDING SESSION — no session-specific Markov snapshot yet",
    };
  }

  if (row.markov_session_status === "closed") {
    return {
      sourceLabel: `${source} · historical session regime`,
      warning: "CLOSED SESSION — Markov snapshot is no longer live for this session",
    };
  }

  if (row.markov_source_scope === "global") {
    return {
      sourceLabel: `${source} · global live regime`,
      warning: "GLOBAL LIVE REGIME — not session-specific",
    };
  }

  if (usesLocalFallback || allTimeframesShareMethod) {
    return {
      sourceLabel: `${source} · local fallback`,
      warning: "LOCAL FALLBACK — 4H/hourly approximated from daily bars",
    };
  }

  return { sourceLabel: source };
}

export type MarkovRegimeSnapshot = {
  session?: string;
  date?: string | null;
  created_at?: number | null;
  probability_type?: "filtered" | string;
  source?: string;
  symbols?: Record<string, MarkovSymbolState>;
};

interface MarkovWorkspaceProps {
  sessionFilter: MarkovSessionFilter;
}

type MarkovSessionKey = "asia" | "us";

type MarkovSessionStates = Record<MarkovSessionKey, QueryState<MarkovRegimeSnapshot>>;

interface MarkovWorkspaceInnerProps extends MarkovWorkspaceProps {
  states: MarkovSessionStates;
}

export const MARKOV_WORKSPACE_TABS: readonly { key: MarkovWorkspaceTab; label: string; description: string }[] = [
  {
    key: "regime-transitions",
    label: "Regime transitions",
    description: "LOG-ONLY transition evidence, isolated from FBE forecasts and Accountability.",
  },
  {
    key: "session-states",
    label: "Session states",
    description: "Separate workspace for Markov state traces by Asia/US session with Kronos context.",
  },
];

const MARKOV_SYMBOL_ORDER = ["ES", "NQ", "GC", "CL", "HG"] as const;

/**
 * MarkovWorkspace is intentionally separate from Command Center,
 * Accountability, and FBE forecast/card code.
 *
 * This component owns Markov regime UI plus read-only Kronos forecast context.
 * It must not import FBE modules, mutate forecast fields, participate in
 * Command Center tabs, or participate in live execution decisions.
 */
export function MarkovWorkspace({ sessionFilter }: MarkovWorkspaceProps) {
  const asiaState = useQueryWithState(api.queries.getLatestMarkovRegime, { session: "asia" }) as QueryState<MarkovRegimeSnapshot>;
  const usState = useQueryWithState(api.queries.getLatestMarkovRegime, { session: "us" }) as QueryState<MarkovRegimeSnapshot>;

  return <MarkovWorkspaceInner sessionFilter={sessionFilter} states={{ asia: asiaState, us: usState }} />;
}

export function MarkovWorkspaceInner({ sessionFilter, states }: MarkovWorkspaceInnerProps) {
  const [activeWorkspace, setActiveWorkspace] = useState<MarkovWorkspaceTab>("regime-transitions");
  const currentFilter = sessionFilter === "all" ? "All sessions" : sessionFilter === "asia" ? "Asia session" : "US session";

  return (
    <main className="markov-view" aria-label="Markov regime workspace">
      <section className="markov-hero">
        <div>
          <span className="markov-kicker">LOG-ONLY · separate tab · separate workspace</span>
          <h2>Markov Regime</h2>
          <p>Dedicated workspace for filtered regime observability. No FBE forecast/card code and no executable trading behavior.</p>
        </div>
        <div className="markov-status">{currentFilter}</div>
      </section>

      <nav className="markov-workspace-tabs" aria-label="Markov workspaces">
        {MARKOV_WORKSPACE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`markov-workspace-tab ${activeWorkspace === tab.key ? "active" : ""}`}
            onClick={() => setActiveWorkspace(tab.key)}
          >
            <strong>{tab.label}</strong>
            <span>{tab.description}</span>
          </button>
        ))}
      </nav>

      {activeWorkspace === "regime-transitions" ? (
        <MarkovRegimeTransitionsWorkspace sessionFilter={sessionFilter} states={states} />
      ) : (
        <MarkovSessionStatesWorkspace sessionFilter={sessionFilter} states={states} />
      )}
    </main>
  );
}

function MarkovRegimeTransitionsWorkspace({ sessionFilter, states }: MarkovWorkspaceInnerProps) {
  const sessions = filteredSessions(sessionFilter);

  return (
    <section className="markov-workspace" aria-label="Markov regime transitions workspace">
      <WorkspaceHeader title="Regime transitions" sessionFilter={sessionFilter} />
      <div className="markov-session-grid">
        {sessions.map((item) => (
          <section key={item.key} className="markov-session-panel active" aria-label={`Markov ${item.label} regime transitions`}>
            <h3>{item.label} session</h3>
            <SessionMeta state={states[item.key]} />
            <p className="markov-panel-note">Filtered real-time probabilities only: p(state_t | data_1:t). No smoothed hindsight probabilities.</p>
            <QueryStateBlock state={states[item.key]}>
              {(data) => <MarkovSymbolGrid snapshot={data} mode="transition" />}
            </QueryStateBlock>
          </section>
        ))}
      </div>
    </section>
  );
}

function MarkovSessionStatesWorkspace({ sessionFilter, states }: MarkovWorkspaceInnerProps) {
  const sessions = filteredSessions(sessionFilter);

  return (
    <section className="markov-workspace" aria-label="Markov session states workspace">
      <WorkspaceHeader title="Session states" sessionFilter={sessionFilter} />
      <div className="markov-session-grid">
        {sessions.map((item) => (
          <section key={item.key} className="markov-session-panel active" aria-label={`Markov ${item.label} session states`}>
            <h3>{item.label} session</h3>
            <SessionMeta state={states[item.key]} />
            <p className="markov-panel-note">Current hidden-state traces with read-only Kronos forecast context. LOG-ONLY; not wired to Command Center or execution.</p>
            <QueryStateBlock state={states[item.key]}>
              {(data) => <MarkovSymbolGrid snapshot={data} mode="state" />}
            </QueryStateBlock>
          </section>
        ))}
      </div>
    </section>
  );
}

function WorkspaceHeader({ title, sessionFilter }: { title: string; sessionFilter: MarkovSessionFilter }) {
  return (
    <div className="markov-workspace-header">
      <h3>{title}</h3>
      <div className="markov-meta-row">
        <span>Source: liveMarketSnapshots</span>
        <span>Probability: filtered</span>
        <span>{sessionFilter === "all" ? "Asia and US queried independently" : "Selected session queried directly"}</span>
      </div>
    </div>
  );
}

function SessionMeta({ state }: { state: QueryState<MarkovRegimeSnapshot> }) {
  const snapshot = state.status === "success" ? state.data : null;
  return (
    <div className="markov-meta-row" aria-label="Markov session metadata">
      <span>Source: {snapshot?.source ?? "liveMarketSnapshots"}</span>
      <span>Session: {snapshot?.session ?? "loading"}</span>
      <span>Status: {formatSessionStatus(snapshot)}</span>
      <span>Freshness: {formatFreshness(snapshot?.created_at)}</span>
    </div>
  );
}

function QueryStateBlock({ state, children }: { state: QueryState<MarkovRegimeSnapshot>; children: (data: MarkovRegimeSnapshot) => ReactNode }) {
  if (state.status === "loading") return <LoadingState label="Markov regime" block />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.retry} label="Markov regime" block />;
  if (!state.data.symbols || Object.keys(state.data.symbols).length === 0) {
    return <EmptyState message="No live Markov regime snapshot is available yet." block />;
  }
  return <>{children(state.data)}</>;
}

function MarkovSymbolGrid({ snapshot, mode }: { snapshot: MarkovRegimeSnapshot; mode: "transition" | "state" }) {
  const rows = useMemo(() => orderedSymbols(snapshot.symbols ?? {}), [snapshot.symbols]);

  return (
    <div className="markov-symbol-grid">
      {rows.map((row) => {
        const quality = markovQuality(row);
        return (
          <article key={row.symbol} className={`markov-symbol-card ${quality.warning ? "markov-symbol-card-warning" : ""}`}>
            <div className="markov-symbol-head">
              <strong>{row.symbol}</strong>
              <span>{formatFreshness(row.created_at)}</span>
            </div>
            {quality.warning ? <div className="markov-quality-warning">{quality.warning}</div> : null}
            {mode === "transition" ? <TransitionRows row={row} /> : <StateRows row={row} quality={quality} />}
          </article>
        );
      })}
    </div>
  );
}

function TransitionRows({ row }: { row: MarkovSymbolState }) {
  return (
    <div className="markov-state-list">
      <StateLine label="Daily" state={row.daily} />
      <StateLine label="4H" state={row.four_hour} />
      <StateLine label="Hourly" state={row.hourly} />
      <StateLine label="Composite" state={row.composite} confidenceMode />
    </div>
  );
}

function StateRows({ row, quality }: { row: MarkovSymbolState; quality: MarkovQuality }) {
  return (
    <div className="markov-state-list">
      <StateLine label="Active" state={row.daily} />
      <StateLine label="Fast" state={row.hourly} />
      <StateLine label="Blend" state={row.composite} confidenceMode />
      <KronosContextBlock kronos={row.kronos} />
      <CombinedRead row={row} />
      <div className="markov-source-line">{quality.sourceLabel} · {row.session ?? "session"}</div>
    </div>
  );
}

function KronosContextBlock({ kronos }: { kronos?: MarkovSymbolState["kronos"] }) {
  if (!kronos?.daily && !kronos?.four_hour && !kronos?.hourly) return null;
  return (
    <div className="markov-kronos-context" aria-label="Kronos forecast context">
      <div className="markov-context-title">Kronos forecast context</div>
      <KronosLine label="Daily" state={kronos.daily} />
      <KronosLine label="4H" state={kronos.four_hour} />
      <KronosLine label="Hourly" state={kronos.hourly} />
    </div>
  );
}

function KronosLine({ label, state }: { label: string; state?: KronosTimeframe }) {
  if (state?.valid === false) {
    return (
      <div className="markov-kronos-line markov-kronos-line-invalid">
        <span>{label}</span>
        <strong>INVALID</strong>
        <em>hidden</em>
        <small>{state.invalid_reason === "invalid_ohlc_range" ? "bad range" : "failed validation"}</small>
      </div>
    );
  }

  const direction = state?.direction ? state.direction.toUpperCase() : "N/A";
  const confidence = formatPercent(state?.confidence);
  const target = typeof state?.predicted_close === "number" ? formatPrice(state.predicted_close) : "—";
  return (
    <div className="markov-kronos-line">
      <span>{label}</span>
      <strong className={directionClass(state?.direction)}>{direction}</strong>
      <em>{confidence}</em>
      <small>{target}</small>
    </div>
  );
}

function CombinedRead({ row }: { row: MarkovSymbolState }) {
  const regime = formatRegime(row.composite?.regime ?? row.daily?.regime);
  const forecast = row.kronos?.four_hour?.direction ?? row.kronos?.hourly?.direction ?? row.kronos?.daily?.direction;
  if (regime === "N/A" && !forecast) return null;
  return (
    <div className="markov-combined-read" aria-label="Combined Markov and Kronos read">
      Markov: {regime} · Kronos: {forecast ? forecast.toUpperCase() : "N/A"}
    </div>
  );
}

function StateLine({ label, state, confidenceMode = false }: { label: string; state?: MarkovTimeframe; confidenceMode?: boolean }) {
  const value = confidenceMode ? state?.confidence : state?.probability;
  return (
    <div className="markov-state-line">
      <span>{label}</span>
      <strong className={regimeClass(state?.regime)}>{formatRegime(state?.regime)}</strong>
      <em>{formatPercent(value)}</em>
    </div>
  );
}

function orderedSymbols(symbols: Record<string, MarkovSymbolState>) {
  return MARKOV_SYMBOL_ORDER.map((symbol) => symbols[symbol]).filter((row): row is MarkovSymbolState => Boolean(row));
}

function formatSessionStatus(snapshot?: MarkovRegimeSnapshot | null) {
  const rows = Object.values(snapshot?.symbols ?? {});
  const status = rows.find((row) => row.markov_session_status)?.markov_session_status;
  if (status === "active") return "session-specific";
  if (status === "pending") return "pending";
  if (status === "closed") return "closed";
  const scope = rows.find((row) => row.markov_source_scope)?.markov_source_scope;
  if (scope === "global") return "global live";
  return "unknown";
}

function filteredSessions(sessionFilter: MarkovSessionFilter): readonly { key: MarkovSessionKey; label: string }[] {
  const sessions = [
    { key: "asia", label: "Asia" },
    { key: "us", label: "US" },
  ] as const;

  if (sessionFilter === "all") return sessions;
  return sessions.filter((item) => item.key === sessionFilter);
}

function formatRegime(regime?: string) {
  return regime ? regime.replace(/_/g, " ").toUpperCase() : "N/A";
}

function formatPercent(value?: number) {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

function formatPrice(value?: number) {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return value >= 1000 ? value.toFixed(1) : value.toFixed(2);
}

function formatFreshness(createdAt?: number | null) {
  if (!createdAt) return "unknown";
  const ageMs = Date.now() - createdAt;
  if (!Number.isFinite(ageMs) || ageMs < 0) return "fresh";
  const ageMin = Math.round(ageMs / 60000);
  return ageMin < 1 ? "<1m" : `${ageMin}m`;
}

function regimeClass(regime?: string) {
  if (regime === "trending_up") return "markov-regime-up";
  if (regime === "trending_down") return "markov-regime-down";
  if (regime === "ranging") return "markov-regime-range";
  return "";
}

function directionClass(direction?: string) {
  if (direction === "up") return "markov-regime-up";
  if (direction === "down") return "markov-regime-down";
  return "markov-regime-range";
}
